import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/context/AuthProvider";
import { useSecurity } from "../../auth/registered/SEC_03_03_SecurityContext";
import { useCompanyContext } from "../../registered/common/company/CompanyContext";
import { fetchCorporateRegistry } from "../../registered/corporate_details/corporateInteractionService";
import { GoldenSourceHelper } from "../../app/Switch_Core_React_Standards";

export default function RegisteredDashboard() {
  const navigate = useNavigate();

  /**
   * 🎯 ARCHITECTURAL SYNC
   * Pulling matrix, isInitialising, and user from AuthProvider.
   * isInitialising handles the boot sequence (F5).
   * matrix presence handles the post-login sequence.
   */
  const { matrix, isInitialising, user, logout } = useAuth();

  const handleSignOut = async () => {
    await logout();
    navigate("/auth/sign-in");
  };
  const { myCorporateEntityIds, hasCapability } = useSecurity();
  const representsAnyCompany = myCorporateEntityIds.length > 0;
  const { activeCompanyId, activeCompanyName, setActiveCompany } = useCompanyContext();

  const siteKey = process.env.REACT_APP_SITE_KEY;
  const [companyPicker, setCompanyPicker] = useState({ open: false, loading: false, options: [] });

  /**
   * 🎯 MANAGE A COMPANY
   * Already-active company: go straight to its dashboard, no re-fetch. A
   * single represented company with none active yet is picked silently.
   * Multiple companies open a lightweight picker instead of forcing a
   * second screen just to choose.
   */
  const handleWorkWithCompany = async () => {
    if (!representsAnyCompany) return;

    if (activeCompanyId && activeCompanyName) {
      navigate("/registered/company-dashboard");
      return;
    }

    setCompanyPicker((prev) => ({ ...prev, loading: true }));
    const registry = await fetchCorporateRegistry({ siteKey });
    const options = registry
      .filter((c) => myCorporateEntityIds.includes(String(GoldenSourceHelper.get(c, "CorporateEntityId"))))
      .map((c) => ({
        id: GoldenSourceHelper.get(c, "CorporateEntityId"),
        name: GoldenSourceHelper.get(c, "EntityName"),
      }));

    if (options.length <= 1) {
      setCompanyPicker({ open: false, loading: false, options: [] });
      const only = options[0] || { id: myCorporateEntityIds[0], name: null };
      await setActiveCompany(only.id, only.name);
      navigate("/registered/company-dashboard");
      return;
    }

    setCompanyPicker({ open: true, loading: false, options });
  };

  const handlePickCompany = async (company) => {
    setCompanyPicker({ open: false, loading: false, options: [] });
    await setActiveCompany(company.id, company.name);
    navigate("/registered/company-dashboard");
  };

  // --- 🛰️ ENVIRONMENT CONFIGURATION ---
  // Using the specific keys provided in your .env
  const SITE_NAME = process.env.REACT_APP_SITE_NAME || "sustina.nrgpix.ch";
  const DEFAULT_LABEL = process.env.REACT_APP_SITE_DEFAULTLABEL || "EFRAG";
  
  // Role Mapping (Ensuring strict casing matches from .env)
  const GA_ROLE = (process.env.REACT_APP_GA || "GLOBALADMIN").toUpperCase();
  const SA_ROLE = (process.env.REACT_APP_SA || "SITEADMIN").toUpperCase();

  /**
   * 🎯 AUTHORITY CHECK
   * Calculates the role based on the matrix provided by the AuthProvider.
   * Handles casing sensitivity (roleCode vs RoleCode) from the BFF.
   */
  const isGlobalAdmin = matrix?.some(
    (m) => String(m.roleCode || m.RoleCode || "").toUpperCase() === GA_ROLE
  );

  const isSiteAdmin = matrix?.some(
    (m) => String(m.roleCode || m.RoleCode || "").toUpperCase() === SA_ROLE
  );

  /**
   * 🎯 THE ORDER OF OPERATIONS GATE
   * We do not render the dashboard until the AuthProvider signals that
   * initialization is complete. If we have a user identity but the security 
   * matrix is still empty, we show the loading state to prevent a race condition.
   */
  if (isInitialising) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status"></div>
          <p className="text-muted fw-bold">Synchronizing {DEFAULT_LABEL} Security Matrix...</p>
        </div>
      </div>
    );
  }

  // 🎯 TOP-LEVEL DASHBOARD: kept deliberately minimal (3 cards max for a
  // company-representing user: Register Company, Request Sustina Roles,
  // Manage <Company>). Everything company-scoped (Corporate Details,
  // Data-Entry, Evidences, Monitoring, Draft and reviews, Commentaries &
  // Publication) now lives one level deeper, as action tiles on the Company
  // Dashboard (company_dashboard/CompanyDashboardView.jsx) reached via the
  // "Manage" card below - not duplicated here.
  const capabilities = [
    {
      name: "Request Sustina Roles",
      icon: "/icons/2D2C31_HELP.png",
      path: "/registered/request-roles",
      description: "Ask for the platform roles you need — approval is handled by an administrator.",
    },
    {
      name: "Enter ESG Data",
      icon: "/icons/2D2C31_enterData.svg",
      path: "/registered/site-admin/report-management/conduct",
      description: "Choose the company, then the pillar (Environmental, Social or Governance), then the topic to report on.",
      restricted: true, // Requires Admin level
    },
    {
      name: "Knowledge Base",
      icon: "/icons/2D2C31_document.png",
      path: "/registered/site-admin/knowledge-base",
      description: "Add, edit and retire the guidance, standards and policies everyone can search while reporting.",
      requiresCapability: "KBADMIN",
    },
    {
      name: "Configure ESG Templates",
      icon: "/icons/2D2C31_defineReport.png",
      path: "/registered/site-admin/report-management",
      description: "Set up the reporting: register a company, build components and templates, define the data that fills them.",
      restricted: true, // Requires Admin level
    },
    {
      name: "Site Administration",
      icon: "/icons/2D2C31_SiteAdmin.png",
      path: "/registered/site-admin",
      description: "Platform administration — the security matrix, the Knowledge Base and the XBRL taxonomy processor.",
      restricted: true, // Requires Admin level
    },
  ];

  /**
   * 🎯 DYNAMIC FILTERING
   * Only includes restricted items if the user identity has the Global Admin 
   * OR Site Admin role as defined in your .env variables.
   */
  const filteredCapabilities = capabilities.filter(cap => {
    if (cap.requiresCapability && !hasCapability(cap.requiresCapability)) return false;
    if (cap.restricted && !isGlobalAdmin && !isSiteAdmin) return false;
    if (cap.requiresCompany && !isGlobalAdmin && !isSiteAdmin && !representsAnyCompany) return false;
    return true;
  });

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-4">
              <div className="col">
                <h2 className="h4 fw-bold">{SITE_NAME}</h2>
                <p className="text-muted small mb-0">
                  Welcome back, <strong>{user?.fullName || user?.userName || user?.email || "User"}</strong>.
                  Choose a capability below.
                </p>
                {activeCompanyName && (
                  <p className="text-primary extra-small fw-bold text-uppercase mb-0 mt-1">
                    <i className="bi bi-building me-1"></i> Working with: {activeCompanyName}
                  </p>
                )}
              </div>
            </div>

            <div className="row g-4 justify-content-center">
              {representsAnyCompany && (
                <div className="col-12 col-sm-6 col-md-4 position-relative">
                  <div
                    className="card h-100 text-center shadow-sm border dashboard-card"
                    onClick={handleWorkWithCompany}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      border: "1px solid #dee2e6"
                    }}
                  >
                    <div className="card-body d-flex flex-column align-items-center justify-content-center py-4">
                      <img
                        src="/icons/2D2C31_user-writer.png"
                        alt="Manage Company"
                        className="mb-3"
                        style={{ width: "64px", height: "64px", objectFit: "contain" }}
                      />
                      <h6 className="card-title mb-1 fw-bold">
                        {companyPicker.loading
                          ? "Loading..."
                          : activeCompanyName
                          ? `Manage "${activeCompanyName}"`
                          : "Manage Company"}
                      </h6>
                      <p className="text-muted small mb-0 px-2">
                        Open a company you represent — its corporate details, data entry,
                        evidence and reporting.
                      </p>
                    </div>
                  </div>

                  {companyPicker.open && (
                    <div
                      className="card shadow border-0 position-absolute start-0 mt-2"
                      style={{ width: "100%", zIndex: 20 }}
                    >
                      <div className="card-body p-2">
                        <span className="extra-small fw-bold text-muted text-uppercase d-block mb-2 px-2">
                          Choose a company
                        </span>
                        {companyPicker.options.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className={`btn btn-sm w-100 text-start mb-1 ${String(c.id) === String(activeCompanyId) ? "btn-dark" : "btn-light"}`}
                            onClick={() => handlePickCompany(c)}
                          >
                            {c.name || `Company #${c.id}`}
                          </button>
                        ))}
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-muted w-100 text-start extra-small"
                          onClick={() => setCompanyPicker({ open: false, loading: false, options: [] })}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {filteredCapabilities.map((cap, index) => (
                <div key={index} className="col-12 col-sm-6 col-md-4">
                  <div
                    className="card h-100 text-center shadow-sm border dashboard-card"
                    onClick={() => navigate(cap.path)}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      border: "1px solid #dee2e6"
                    }}
                  >
                    <div className="card-body d-flex flex-column align-items-center justify-content-center py-4">
                      <img
                        src={cap.icon}
                        alt={cap.name}
                        className="mb-3"
                        style={{ width: "64px", height: "64px", objectFit: "contain" }}
                      />
                      <h6 className="card-title mb-1 fw-bold">{cap.name}</h6>
                      {cap.description && (
                        <p className="text-muted small mb-0 px-2">{cap.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 5. FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
              <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={handleSignOut}>
                <i className="bi bi-box-arrow-left me-2"></i> Sign Out
              </button>
              <div className="text-end">
                <span className="text-muted extra-small d-block">Workflow Context</span>
                <span className="fw-bold small">{activeCompanyName || SITE_NAME}</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}